// src/components/Sidebar.js
import React, { useState, useEffect } from 'react';
import { Button, ButtonGroup } from '@mui/material';
import { db } from '../../firebase';
import { collection, query, where, orderBy, onSnapshot } from 'firebase/firestore';
import './Chat.css';

const SidebarSkeleton = () => (
  <div className="chat-list-skeleton">
    {Array.from({ length: 6 }).map((_, index) => (
      <div key={index} className="chat-item chat-item-skeleton">
        <div className="chat-avatar skeleton-block" />
        <div className="chat-content">
          <div className="chat-top-row">
            <span className="chat-name skeleton-line skeleton-line-md" />
            <span className="chat-date skeleton-line skeleton-line-xs" />
          </div>
          <div className="chat-ad-title skeleton-line skeleton-line-lg" />
          <div className="chat-last-message skeleton-line skeleton-line-md" />
        </div>
      </div>
    ))}
  </div>
);

const Sidebar = ({ currentUser, onSelectRoom, selectedRoomId }) => {
  const [chatRooms, setChatRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('All');
  const currentUserId = currentUser?.uid;

  useEffect(() => {
    if (!currentUserId) return;

    // 1. Query: Get all rooms where user is a participant AND hasMessages is true
    const q = query(
      collection(db, 'chat_rooms'),
      where('participantIds', 'array-contains', currentUserId),
      orderBy('lastMessageTimestamp', 'desc')
    );

    // 2. Real-time Listener
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const roomsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setChatRooms(roomsData);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [currentUserId]);

  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    const date = timestamp.toDate();
    const now = new Date();
    // If it's today, show time (e.g., 14:30). If older, show date (e.g., Mar 29)
    if (date.toDateString() === now.toDateString()) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    }
    return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
  };

  if (!currentUserId) {
    return (
      <div className="sidebar">
        <div className="sidebar-header">
          <h2>INBOX</h2>
        </div>
        <div className="chat-list-empty">
          <div className="chat-list-empty-icon">🔒</div>
          <h3>Sign in to see your chats</h3>
          <p>Please log in to access your conversations.</p>
        </div>
      </div>
    );
  }

  const filteredRooms = chatRooms.filter(room => {
    if (filter === 'Unread') {
      return room.unread_counts?.[currentUserId] > 0;
    }
    if (filter === 'Important') {
      // Placeholder for important filter logic
      return room.isImportant || false; 
    }
    return true;
  });

  return (
    <div className="sidebar">

      {/* HEADER */}
      <div className="sidebar-header">
        <h2>INBOX</h2>
        {/* <div className="header-icons">
          🔍 ⋮
        </div> */}
      </div>

      {/* FILTERS */}
      {chatRooms.length === 0 && !loading ? null : (
        <div className="quick-filters">
          <div className="filter-title">Quick Filters</div>
          <div className="filter-pills">
            <button className={`pill ${filter === 'All' ? 'active' : ''}`} onClick={() => setFilter('All')}>
              <span>All</span>
            </button>
            <button className={`pill ${filter === 'Unread' ? 'active' : ''}`} onClick={() => setFilter('Unread')}>
              <span>Unread</span>
            </button>
            <button className={`pill ${filter === 'Important' ? 'active' : ''}`} onClick={() => setFilter('Important')}>
              <span>Important</span>
            </button>
          </div>
        </div>
      )}

      {/* VERIFIED */}
      {/* <div className="verified-banner">
      <span>See all Verified Users</span>
      <span>›</span>
    </div> */}

      {/* CHAT LIST */}
      <div className="chat-list-container">
        {loading ? (
          <SidebarSkeleton />
        ) : chatRooms.length === 0 ? (
          <div className="chat-list-empty">
            <div className="chat-list-empty-icon">...</div>
            <h3>No conversations yet</h3>
            <p>Your chats with buyers and sellers will appear here once a message is sent.</p>
          </div>
        ) : filteredRooms.length === 0 ? (
          <div className="chat-list-empty">
            <div className="chat-list-empty-icon">📭</div>
            <h3>No {filter.toLowerCase()} chats</h3>
            <p>You don't have any {filter.toLowerCase()} conversations at the moment.</p>
          </div>
        ) : (
          filteredRooms.map((room) => {
            const otherUserId = room.participantIds.find(id => id !== currentUserId);
            const otherUser = room.participantInfo?.[otherUserId];
            const isSelected = room.id === selectedRoomId;
            const unreadCount = room.unread_counts?.[currentUserId];

            return (
              <div
                key={room.id}
                className={`chat-item ${isSelected ? 'selected' : ''}`}
                onClick={() => onSelectRoom(room)}
              >
                <img
                  src={
                    otherUser?.image ||
                    `https://ui-avatars.com/api/?name=${otherUser?.name || 'U'}`
                  }
                  alt="avatar"
                  className="chat-avatar"
                />

                <div className="chat-content">
                  <div className="chat-top-row">
                    <span className="chat-name">
                      {otherUser?.name || 'Qinto User'}
                    </span>
                    <span className="chat-date">
                      {formatTime(room.lastMessageTimestamp)}
                    </span>
                  </div>

                  <div className="chat-ad-title">
                    {room.adTitle}
                  </div>

                  <div className={`chat-last-message ${unreadCount > 0 ? "unread" : ""}`}>
                    {room.lastMessageText || "Start conversation"}
                  </div>
                </div>
                {/* We can add chat options later like Delete Chat etc., */}
                {/* <div className="chat-options">⋮</div> */}

                {unreadCount > 0 && (
                  <div className="chat-unread-badge">
                    {unreadCount > 99 ? "99+" : unreadCount}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default Sidebar;
